import type { ReactNode } from "react"

import { Badge } from "~/components/design-system/components"
import { DeltaCell } from "~/components/demand-2/shared/demandBreakdownShared"
import { SizingSpinner } from "~/components/demand-2/shared/SizingSpinner"

export const NOT_ANSWERED = "Not answered"

/**
 * For the `ExpandableCardHeader` these columns sit in: room for two stacked answer badges plus
 * padding, in place of the header's fixed `h-11`, so every card is the same height.
 */
export const STRATEGY_CARD_HEADER_CLASS = "h-auto min-h-16 py-2"

/**
 * The right edge of a Demand configuration card header: the answer, the % badges, the unit change.
 * Fixed widths, so the columns line up from card to card.
 */
export const StrategyImpactColumns = ({
  answer,
  afterAnswer,
  badges,
  units,
  unitsLoading = false,
}: {
  answer: string | undefined
  /** Beside the answer, e.g. the own-answers badge; wraps below it rather than widen the column. */
  afterAnswer?: ReactNode
  badges?: ReactNode
  units: number | null
  unitsLoading?: boolean
}) => (
  <>
    <span className="flex w-48 shrink-0 flex-wrap items-center gap-1">
      {answer != null && (
        <Badge
          variant={answer === NOT_ANSWERED ? "yellow" : "gray"}
          size="xs"
          className="max-w-full"
          title={answer}
        >
          <span className="truncate">{answer}</span>
        </Badge>
      )}
      {afterAnswer && <span className="flex max-w-full">{afterAnswer}</span>}
    </span>
    <span className="flex w-32 shrink-0 justify-end gap-1">{badges}</span>
    <span className="flex w-20 shrink-0 justify-end">
      {unitsLoading && <SizingSpinner className="w-16" />}
      {!unitsLoading && units != null && <DeltaCell value={units} />}
    </span>
  </>
)

/** A signed % change, as the cards' impact badge. */
export const PctBadge = ({ pct, title }: { pct: number; title?: string }) => (
  <Badge variant="gray" size="xs" title={title}>
    {pct > 0 ? "+" : ""}
    {pct}%
  </Badge>
)
